import { Injectable, inject } from '@angular/core';
import { singleConnect } from '@core/utils';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import {
	Action,
	State,
	StateToken,
	createPropertySelectors,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { ClientIntakeResponsesSubCollectionRepo } from '../clientIntakeResponses.firebase.repo';

import type {
	ClientIntakeResponse,
	ClientIntakeResponsesStateModel,
} from './clientIntakeResponses.firebase.models';
import {
	ClearClientIntakeResponsesConnectionAndState,
	SetClientIntakeResponses,
	SetClientIntakeResponsesConnection,
	UpsertClientIntakeResponses,
} from './clientIntakeResponses.firebase.actions';
import { of, retry, timer } from 'rxjs';

export const CLIENT_INTAKE_RESPONSES_STATE_TOKEN =
	new StateToken<ClientIntakeResponsesStateModel>('clientIntakeResponses');

export const $PROPS = createPropertySelectors(
	CLIENT_INTAKE_RESPONSES_STATE_TOKEN,
);

export const CLIENT_INTAKE_RESPONSES_STATE_DEFAULTS: ClientIntakeResponsesStateModel =
	{
		query: {
			connected: false,
			pathParams: null,
		},
		items: [],
	};

@State<ClientIntakeResponsesStateModel>({
	name: CLIENT_INTAKE_RESPONSES_STATE_TOKEN,
	defaults: CLIENT_INTAKE_RESPONSES_STATE_DEFAULTS,
})
@Injectable()
export class ClientIntakeResponsesState implements NgxsOnInit {
	private subCollectionRepo = inject(ClientIntakeResponsesSubCollectionRepo);
	static _props = $PROPS;

	ngxsOnInit(): void {
		singleConnect(
			ClientIntakeResponsesState._props.query,
			(query) => {
				if (
					!query.connected ||
					!query.pathParams?.businessId ||
					!query.pathParams?.matterId ||
					!query.pathParams?.clientIntakeFormId
				) {
					return of([] as ClientIntakeResponse[]);
				}

				return this.subCollectionRepo
					.getCollection(query.pathParams)
					.getAll()
					.pipe(
						retry({
							count: 10,
							delay: (error, retryCount) => {
								const backoffTime = Math.min(
									500 * Math.pow(2, retryCount - 1),
									30000,
								);
								return timer(backoffTime);
							},
						}),
					);
			},
			(data) => {
				return new SetClientIntakeResponses(data);
			},
		);
	}

	@Action(SetClientIntakeResponsesConnection)
	setClientIntakeResponsesConnection(
		ctx: StateContext<ClientIntakeResponsesStateModel>,
		action: SetClientIntakeResponsesConnection,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: action.connect,
					pathParams: action.pathParams,
				}),
			}),
		);
	}

	@Action(ClearClientIntakeResponsesConnectionAndState)
	clearClientIntakeResponsesConnection(
		ctx: StateContext<ClientIntakeResponsesStateModel>,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: false,
					pathParams: null,
				}),
				items: [],
			}),
		);
	}

	@Action(SetClientIntakeResponses)
	setClientIntakeResponses(
		ctx: StateContext<ClientIntakeResponsesStateModel>,
		{ items }: SetClientIntakeResponses,
	): void {
		ctx.patchState({
			items,
		});
	}

	@Action(UpsertClientIntakeResponses)
	upsertClientIntakeResponses(
		ctx: StateContext<ClientIntakeResponsesStateModel>,
		{ clientIntakeResponses }: UpsertClientIntakeResponses,
	): void {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams) return;

			clientIntakeResponses.forEach((clientIntakeResponse) => {
				void this.subCollectionRepo
					.getCollection(pathParams)
					?.set(clientIntakeResponse.id, clientIntakeResponse);
			});
		} catch (err) {
			const responseIds = clientIntakeResponses?.map((r) => r.id) ?? [];
			console.error(
				`Error upserting client intake responses (IDs: ${JSON.stringify(responseIds)}):`,
				err,
			);
		}
	}

	// @Action(UpdateClientIntakeForm)
	// updateClientIntakeForm(
	// 	ctx: StateContext<ClientIntakeResponsesStateModel>,
	// 	{ clientIntakeForm }: UpdateClientIntakeForm,
	// ): void {
	// 	this.repo?.update(clientIntakeForm.id, clientIntakeForm);
	// }
}
